import { memo, useState, useCallback, useEffect } from "react";
import { useI18n } from "@/i18n";
import { useRole } from "@/hooks/useRole";
import { Pagination } from "@/design-system";
import { listInventoryBalance, closePeriod } from "@/infrastructure/inventory-balance/inventoryBalanceService";
import { listProducts } from "@/infrastructure/product/productService";
import { listWarehouses } from "@/infrastructure/warehouse/warehouseService";
import { listCategories } from "@/infrastructure/category/categoryService";
import type { InventoryBalance, ProductMeta, Product, WarehouseRecord, Location, Category } from "@/domain/types";
import { colors, typography, transitions } from "@/design-system/tokens";

const PAGE_SIZE = 8;
const now = new Date();
const CURRENT_YEAR = now.getFullYear();
const CURRENT_MONTH = now.getMonth() + 1;

export const BalancePage = memo(function BalancePage() {
  const { t } = useI18n();
  const { canClosePeriod } = useRole();
  const [items, setItems] = useState<InventoryBalance[]>([]);
  const [meta, setMeta] = useState<ProductMeta>({ totalItems: 0, itemsPerPage: PAGE_SIZE, totalPages: 0, currentPage: 1 });
  const [products, setProducts] = useState<Product[]>([]);
  const [warehouses, setWarehouses] = useState<WarehouseRecord[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [year, setYear] = useState(CURRENT_YEAR);
  const [month, setMonth] = useState(CURRENT_MONTH);
  const [warehouseFilter, setWarehouseFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [categories, setCategories] = useState<Category[]>([]);
  const [showClose, setShowClose] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [balanceData, prodData, whData] = await Promise.all([
        listInventoryBalance({ page, limit: PAGE_SIZE, periodYear: year, periodMonth: month, warehouseId: warehouseFilter || undefined, categoryId: categoryFilter || undefined }),
        products.length === 0 ? listProducts({ limit: 200 }).then(r => r.items) : Promise.resolve(products),
        warehouses.length === 0 ? listWarehouses() : Promise.resolve(warehouses),
      ]);
      setItems(balanceData.items);
      setMeta(balanceData.meta);
      if (products.length === 0) setProducts(prodData as Product[]);
      if (warehouses.length === 0) setWarehouses(whData as WarehouseRecord[]);
    } catch {}
    setLoading(false);
  }, [page, year, month, warehouseFilter, categoryFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => { listCategories({ limit: 100, status: "ACTIVE" }).then(res => setCategories(res.items)).catch(() => {}); }, []);

  useEffect(() => {
    if (items.length > 0 && locations.length === 0) {
      import("@/infrastructure/location/locationService").then(mod => {
        const whIds = [...new Set(items.map(i => i.warehouseId))];
        Promise.all(whIds.map(id => mod.listLocationsByWarehouse(id))).then(results => {
          setLocations(results.flat());
        });
      });
    }
  }, [items, locations.length]);

  const showToast = useCallback((msg: string, type: "success" | "error") => { setToast({ message: msg, type }); setTimeout(() => setToast(null), 3500); }, []);

  const getProductName = useCallback((id: string, code?: string, name?: string) => {
    if (code) return code;
    const p = products.find(pr => pr.id === id);
    return p ? p.code : id.slice(0, 8);
  }, [products]);

  const getLocationCode = useCallback((id: string) => {
    const l = locations.find(loc => loc.id === id);
    return l ? l.locationCode : id.slice(0, 8);
  }, [locations]);

  const handleClosePeriod = useCallback(async () => {
    try {
      await closePeriod(year, month);
      setShowClose(false);
      showToast(t.balance.successClosed, "success");
      fetchData();
    } catch (err: any) {
      setShowClose(false);
      const msg = err?.response?.data?.message ?? err?.message ?? "";
      if (typeof msg === "string" && msg.includes("already closed")) showToast(t.balance.errorAlreadyClosed, "error");
      else if (typeof msg === "string" && msg.includes("pending")) showToast(t.balance.errorPendingMovements, "error");
      else if (typeof msg === "string" && msg.includes("No balance")) showToast(t.balance.errorNotFound, "error");
      else showToast(msg || "Error", "error");
    }
  }, [year, month, fetchData, showToast, t]);

  const hasOpenRecords = items.some(i => i.status === "OPEN");
  const years = Array.from({ length: CURRENT_YEAR - 2023 }, (_, i) => 2024 + i);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px", padding: "36px 36px 48px", minHeight: "100vh", background: "#fff", boxSizing: "border-box" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <h1 style={{ fontFamily: typography.fontFamily, fontSize: "1.9rem", fontWeight: typography.weight.bold, color: "#111", margin: 0, letterSpacing: "-0.03em" }}>{t.balance.title}</h1>
          <p style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#999", margin: "4px 0 0" }}>{t.balance.subtitle}</p>
        </div>
        {hasOpenRecords && canClosePeriod && (
          <button onClick={() => setShowClose(true)} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#fff", background: "#111", border: "none", borderRadius: "10px", padding: "10px 20px", cursor: "pointer", transition: `background ${transitions.fast}` }} onMouseEnter={e => ((e.currentTarget as HTMLButtonElement).style.background = "#333")} onMouseLeave={e => ((e.currentTarget as HTMLButtonElement).style.background = "#111")}>
            {t.balance.closePeriod}
          </button>
        )}
      </div>

      <div style={{ display: "flex", gap: "16px", alignItems: "flex-end", flexWrap: "wrap" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <label style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, fontWeight: typography.weight.semibold, color: "#888", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t.balance.year}</label>
          <select value={year} onChange={e => { setYear(Number(e.target.value)); setPage(1); }} style={selectStyle}>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <label style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, fontWeight: typography.weight.semibold, color: "#888", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t.balance.month}</label>
          <select value={month} onChange={e => { setMonth(Number(e.target.value)); setPage(1); }} style={selectStyle}>
            {t.balance.months.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <label style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, fontWeight: typography.weight.semibold, color: "#888", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t.dispatches.warehouseLabel}</label>
          <select value={warehouseFilter} onChange={e => { setWarehouseFilter(e.target.value); setPage(1); }} style={{ ...selectStyle, minWidth: "160px" }}>
            <option value="">—</option>
            {warehouses.map(w => <option key={w.warehouse_id} value={w.warehouse_id}>{w.warehouse_name}</option>)}
          </select>
        </div>
        <select value={categoryFilter} onChange={e => { setCategoryFilter(e.target.value); setPage(1); }} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: categoryFilter ? "#111" : "#666", background: "#fff", border: "1px solid #e8e8e8", borderRadius: "8px", padding: "9px 14px", outline: "none", cursor: "pointer" }}>
          <option value="">{t.products.filterAllCategories}</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      <div style={{ background: "#fff", borderRadius: "20px", border: "1px solid #e8e8e8", overflow: "hidden", minHeight: "300px" }}>
        {loading && <LoadingState />}
        {!loading && items.length === 0 && <EmptyState />}
        {!loading && items.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #f2f2f2" }}>
                  {[t.balance.colProduct, t.balance.colLocation, t.products.colCategory, t.balance.colOpening, t.balance.colInflows, t.balance.colOutflows, t.balance.colClosing, t.balance.colStatus].map(col => (
                    <th key={col} style={{ fontFamily: typography.fontFamily, fontSize: "10px", fontWeight: typography.weight.bold, color: "#bbb", textTransform: "uppercase", letterSpacing: "0.1em", padding: "14px 16px", textAlign: "left" }}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <tr key={item.id} style={{ borderBottom: "1px solid #f7f7f7", transition: `background ${transitions.fast}` }} onMouseEnter={e => ((e.currentTarget as HTMLTableRowElement).style.background = "#fafafa")} onMouseLeave={e => ((e.currentTarget as HTMLTableRowElement).style.background = "transparent")}>
                    <td style={cell}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#111" }}>{getProductName(item.productId, item.productCode, item.productName)}</span></td>
                    <td style={cell}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#555" }}>{getLocationCode(item.locationId)}</span></td>
                    <td style={cell}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#888" }}>{item.categoryName ?? "—"}</span></td>
                    <td style={cell}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#888" }}>{item.openingBalance}</span></td>
                    <td style={cell}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#16a34a", fontWeight: typography.weight.semibold }}>+{item.periodInflows}</span></td>
                    <td style={cell}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#dc2626", fontWeight: typography.weight.semibold }}>-{item.periodOutflows}</span></td>
                    <td style={cell}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#111" }}>{item.closingBalance}</span></td>
                    <td style={cell}><StatusBadge status={item.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {meta.totalPages > 1 && (
              <div style={{ padding: "0 16px 14px" }}>
                <Pagination currentPage={meta.currentPage} totalPages={meta.totalPages} totalItems={meta.totalItems} onPageChange={setPage} />
              </div>
            )}
          </div>
        )}
      </div>

      {showClose && <CloseModal year={year} month={month} onConfirm={handleClosePeriod} onClose={() => setShowClose(false)} />}
      {toast && <div role="alert" style={{ position: "fixed", bottom: "24px", right: "24px", background: toast.type === "success" ? "#111" : colors.error, color: "#fff", fontFamily: typography.fontFamily, fontSize: typography.size.sm, padding: "12px 20px", borderRadius: "10px", boxShadow: "0 4px 16px rgba(0,0,0,0.2)", zIndex: 300 }}>{toast.message}</div>}
    </div>
  );
});

function CloseModal({ year, month, onConfirm, onClose }: { year: number; month: number; onConfirm: () => void; onClose: () => void }) {
  const { t } = useI18n();
  const [confirming, setConfirming] = useState(false);
  useEffect(() => { const h = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); }; document.addEventListener("keydown", h); return () => document.removeEventListener("keydown", h); }, [onClose]);

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: "24px" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: "20px", padding: "32px", width: "100%", maxWidth: "420px", display: "flex", flexDirection: "column", gap: "16px" }}>
        <h2 style={{ fontFamily: typography.fontFamily, fontSize: typography.size.lg, fontWeight: typography.weight.bold, color: "#111", margin: 0 }}>{t.balance.closeConfirmTitle}</h2>
        <p style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#666", margin: 0 }}>
          {t.balance.months[month - 1]} {year}
        </p>
        <p style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#888", margin: 0 }}>{t.balance.closeConfirmDesc}</p>
        <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#666", background: "transparent", border: "1px solid #e0e0e0", borderRadius: "9px", padding: "10px 20px", cursor: "pointer" }}>{t.balance.cancel}</button>
          <button onClick={() => { setConfirming(true); onConfirm(); }} disabled={confirming} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#fff", background: confirming ? "#ccc" : "#111", border: "none", borderRadius: "9px", padding: "10px 20px", cursor: confirming ? "not-allowed" : "pointer" }}>{t.balance.closeConfirmBtn}</button>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const { t } = useI18n();
  const isOpen = status === "OPEN";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "5px", background: isOpen ? "rgba(34,197,94,0.1)" : "rgba(0,0,0,0.04)", color: isOpen ? "#16a34a" : "#999", fontFamily: typography.fontFamily, fontSize: "11px", fontWeight: typography.weight.semibold, padding: "4px 10px", borderRadius: "9999px" }}>
      <span style={{ width: "5px", height: "5px", borderRadius: "50%", background: isOpen ? "#16a34a" : "#bbb" }} />
      {isOpen ? t.balance.statusOpen : t.balance.statusClosed}
    </span>
  );
}

function EmptyState() {
  const { t } = useI18n();
  return <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "64px 24px", gap: "10px" }}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#bbb" }}>{t.balance.emptyTitle}</span></div>;
}

function LoadingState() {
  return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "64px 24px" }}><span style={{ width: "24px", height: "24px", border: `3px solid ${colors.primary}`, borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} /></div>;
}

const selectStyle: React.CSSProperties = { fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#111", background: "#fff", border: "1px solid #e8e8e8", borderRadius: "8px", padding: "9px 14px", outline: "none", cursor: "pointer", minWidth: "100px" };
const cell: React.CSSProperties = { padding: "12px 16px", verticalAlign: "middle" };
